package data.db.delight.daos

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import data.db.delight.AppDatabase
import data.db.delight.KinoshkaDbQueries
import data.db.delight.WatchProgress
import features.watch.model.WatchEntry
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import utils.IO_MULTI
import utils.toBoolean

class WatchProgressDao(private val database: AppDatabase) {

    private suspend fun queries() = database.queries()

    suspend fun upsert(entry: WatchEntry) {
        val q = queries() ?: return
        q.transaction {
            q.write(entry)
        }
    }

    suspend fun replaceAll(entries: List<WatchEntry>) {
        val q = queries() ?: return
        q.transaction {
            q.deleteAllWatchProgress()
            entries.forEach { entry -> q.write(entry) }
        }
    }

    suspend fun observe(): Flow<List<WatchProgress>>? {
        return queries()?.getAllWatchProgress()?.asFlow()?.mapToList(Dispatchers.IO_MULTI)
    }

    suspend fun snapshot(): List<WatchEntry> {
        val q = queries() ?: return emptyList()
        return q.getAllWatchProgress().awaitAsList().map { it.toEntry() }
    }

    suspend fun find(videoId: Int, seasonNumber: Int, episodeNumber: Int): WatchEntry? {
        val q = queries() ?: return null
        return q.findWatchProgress(
            videoId = videoId.toLong(),
            seasonNumber = seasonNumber.toLong(),
            episodeNumber = episodeNumber.toLong(),
        ).awaitAsOneOrNull()?.toEntry()
    }
}

private suspend fun KinoshkaDbQueries.write(entry: WatchEntry) {
    insertWatchProgress(
        videoId = entry.videoIdInt.toLong(),
        imdbId = entry.imdbId,
        isTvShow = if (entry.isTvShow) 1L else 0L,
        seasonNumber = entry.seasonNumber.toLong(),
        episodeNumber = entry.episodeNumber.toLong(),
        positionSec = entry.positionSec.toLong(),
        durationSec = entry.durationSec.toLong(),
        progress = entry.progress,
        engagedSec = entry.engagedSec.toLong(),
        status = entry.status,
        title = entry.title,
        imageUrl = entry.imageUrl,
        backdropUrl = entry.backdropUrl,
        hiddenFromContinue = if (entry.hiddenFromContinue) 1L else 0L,
        isDeleted = if (entry.isDeleted) 1L else 0L,
        changedAt = entry.changedAt,
        audio = entry.audio,
        quality = entry.quality,
        subtitles = entry.subtitles,
    )
}

fun WatchProgress.toEntry(): WatchEntry = WatchEntry(
    videoId = videoId.toString(),
    imdbId = imdbId,
    isTvShow = isTvShow.toBoolean(),
    seasonNumber = seasonNumber.toInt(),
    episodeNumber = episodeNumber.toInt(),
    positionSec = positionSec.toInt(),
    durationSec = durationSec.toInt(),
    progress = progress,
    engagedSec = engagedSec.toInt(),
    status = status,
    title = title,
    imageUrl = imageUrl,
    backdropUrl = backdropUrl,
    audio = audio,
    quality = quality,
    subtitles = subtitles,
    hiddenFromContinue = hiddenFromContinue.toBoolean(),
    isDeleted = isDeleted.toBoolean(),
    changedAt = changedAt,
)
